import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { LifeBuoy, Mail, BookOpen, ShieldCheck } from 'lucide-react'
import { useSeo } from '@/lib/useSeo'
import { PageHeader } from '@/components/public/PageHeader'

const faqs: Array<{ q: string; a: string }> = [
  {
    q: 'What is VICE?',
    a: 'VICE (Virgin Islands Commercial Environment) helps capture and validate line level import and export data from existing digital documents and transmit it to CBP\'s Automated Commercial Environment (ACE).',
  },
  {
    q: 'Is VICE an official U.S. government website?',
    a: 'No. VICE is built by MetaPhase as a private sector tool for U.S. Virgin Islands import processing support. We explicitly do not use federal seals or official marks.',
  },
  {
    q: 'How does CBP manage customs in the U.S. Virgin Islands?',
    a: 'The U.S. Virgin Islands sit outside the U.S. customs territory under 19 CFR § 7.2. Imports are governed by territorial law, with duty rates set locally, while CBP enforces applicable federal laws at the border. U.S. trade agreement preferences and Section 321 de minimis generally do not apply to imports into the USVI.',
  },
  {
    q: 'Who can use VICE?',
    a: 'VICE is invitation-only for USVI Agents. There is no public self-signup. If you are a designated agent, you will receive an invitation email.',
  },
  {
    q: 'Does VICE replace ACE?',
    a: 'No. VICE supports review and submission to ACE. It does not replace CBP\'s system of record.',
  },
  {
    q: 'What document formats does VICE accept?',
    a: 'VICE works with existing document formats already used in the trade process to prepare entry data. AWA handles text extraction, field organization, validation, and recommendations so the USVI Agent can review and transmit.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. Every table has Row-Level Security enabled. Data is encrypted in transit (TLS 1.2+) and at rest (AES-256). The application is protected by a strict Content Security Policy, OWASP ZAP baseline scans run on every pull request, and dependencies are monitored continuously by Dependabot and npm audit. See the Security page for details.',
  },
  {
    q: 'Is VICE accessible?',
    a: 'Yes. VICE targets WCAG 2.1 AA and Section 508 on every user-facing surface. We run @axe-core/cli against every public URL in CI and verify keyboard and screen-reader paths before shipping. See our Accessibility statement for the full commitment.',
  },
  {
    q: 'How do I report a security issue?',
    a: 'Please use GitHub Security Advisories on the VICE repository, or email security@metaphase.tech. We acknowledge receipt within 2 business days and prioritize fixes.',
  },
  {
    q: 'How can I get in touch?',
    a: 'Use the contact form on our Contact page. We read every message and reply as quickly as we can.',
  },
]

const helpCards = [
  {
    Icon: Mail,
    title: 'Contact us',
    body: 'For access requests, support questions, or feedback — send a message and we\'ll reply.',
    cta: 'Open contact form',
    to: '/contact',
  },
  {
    Icon: BookOpen,
    title: 'Learn how VICE works',
    body: 'Read about the AI Workflow Automation pipeline that powers entry data review and submission support.',
    cta: 'Visit AWA',
    to: '/awa',
  },
  {
    Icon: ShieldCheck,
    title: 'Security & accessibility',
    body: 'Our security posture, vulnerability reporting, and Section 508 / WCAG 2.1 AA commitment.',
    cta: 'Security overview',
    to: '/security',
  },
] as const

export function Help() {
  useSeo({
    title: 'Help & FAQ',
    description:
      'Get help with VICE: contact, security, accessibility, and answers to common questions about USVI customs, ACE, and AI workflow automation.',
  })

  useEffect(() => {
    const id = 'faq-structured-data'
    const existing = document.getElementById(id)
    const script = existing ?? document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    })
    if (!existing) document.head.appendChild(script)
    return () => {
      const el = document.getElementById(id)
      if (el) el.remove()
    }
  }, [])

  return (
    <>
      <PageHeader
        eyebrow="Help & FAQ"
        title="Need a hand? Start here."
        lede="Get the right answer fast — reach a human, learn the platform, or scan the FAQ."
      />

      {/* General help section */}
      <section aria-labelledby="general-help" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-center gap-2 mb-6">
          <LifeBuoy size={20} className="text-accent-sunset" aria-hidden="true" />
          <h2 id="general-help" className="text-xl font-bold text-primary">
            Get help
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {helpCards.map(({ Icon, title, body, cta, to }) => (
            <article
              key={title}
              className="bg-white rounded-lg border border-neutral-200 p-6 shadow-sm flex flex-col"
            >
              <div className="inline-flex p-3 rounded bg-secondary-lightest text-secondary self-start">
                <Icon size={22} aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-primary">{title}</h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed flex-1">{body}</p>
              <Link
                to={to}
                className="mt-4 inline-flex items-center text-sm font-semibold text-secondary hover:text-primary focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 rounded"
              >
                {cta} →
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ section */}
      <section
        aria-labelledby="faq-heading"
        className="bg-neutral-50 border-y border-neutral-200 py-16"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 id="faq-heading" className="text-2xl sm:text-3xl font-bold text-primary">
            Frequently asked questions
          </h2>
          <p className="mt-2 text-neutral-700">Click any question to expand the answer.</p>

          <div className="mt-8 divide-y divide-neutral-200 border-y border-neutral-200">
            {faqs.map(({ q, a }, idx) => (
              <details key={q} className="group py-4" open={idx === 0}>
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 rounded">
                  <h3 className="text-base sm:text-lg font-semibold text-primary">{q}</h3>
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 text-2xl text-neutral-400 group-open:rotate-45 transition-transform leading-none"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-neutral-700 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>

          <p className="mt-10 text-sm text-neutral-600">
            Still stuck?{' '}
            <Link
              to="/contact"
              className="text-secondary underline decoration-accent-turquoise underline-offset-4 font-semibold"
            >
              Contact us
            </Link>
            .
          </p>
        </div>
      </section>
    </>
  )
}
